/**
 * A closed dropdown with a search box: click it, type to narrow the list, pick one.
 *
 * Renders its list inline (absolutely positioned under the field) rather than in a
 * portal, so it works inside the app's hand-rolled fixed-overlay dialogs without
 * z-index juggling and stays inside their `role="dialog"` subtree.
 *
 * Filtering is client-side over label + value by default. Pass `onSearchChange`
 * to search on the server instead; `options` are then shown as given.
 */
import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Loader2, Search } from 'lucide-react';
import { cn } from './utils';
import { filterOptions, type DropdownOption } from './dropdownOptions';

interface SearchableDropdownProps {
  options: DropdownOption[];
  value: string | null | undefined;
  onChange: (value: string) => void;
  /** Accessible name of the field (the combobox). */
  ariaLabel: string;
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
  loading?: boolean;
  disabled?: boolean;
  /** Server-side search: called with the typed query; `options` are not filtered here. */
  onSearchChange?: (query: string) => void;
  /** Max rows rendered (long lists stay responsive). */
  limit?: number;
  /** Muted note under the list (e.g. "Showing 100 of 214 matches"). */
  footer?: string | null;
  /**
   * What the field shows for `value` when it isn't among `options` (server-side
   * search: the pick stays shown while another search is on screen, without
   * being mixed into those results).
   */
  selectedLabel?: string | null;
  /** Longest search the box accepts (e.g. the server's query limit). */
  searchMaxLength?: number;
}

export function SearchableDropdown({
  options, value, onChange, ariaLabel, placeholder, searchPlaceholder, emptyText,
  loading = false, disabled = false, onSearchChange, limit = 100, footer, selectedLabel, searchMaxLength,
}: SearchableDropdownProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listId = useRef(`dropdown-${Math.random().toString(36).slice(2)}`).current;
  const optionId = (i: number) => `${listId}-option-${i}`;

  const selectedOption = options.find((o) => o.value === value);
  const shownLabel = selectedOption?.label ?? (value ? selectedLabel : null);
  const shown = (onSearchChange ? options : filterOptions(options, query)).slice(0, limit);
  // The keyboard's row, kept on a row that is actually on screen as results change.
  const activeIndex = Math.min(Math.max(active, 0), Math.max(shown.length - 1, 0));

  // Close on a click outside the field or its list.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  useEffect(() => setActive(0), [query, open]);

  // Keep the keyboard's row in view as it moves.
  useEffect(() => {
    if (open) listRef.current?.querySelector(`#${CSS.escape(optionId(activeIndex))}`)?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus(); // back to the field, not the page
  };

  const updateQuery = (q: string) => {
    setQuery(q);
    onSearchChange?.(q);
  };

  const pick = (option: DropdownOption) => {
    // While a newer search is loading, the rows on screen answer the old one.
    if (loading) return;
    onChange(option.value);
    close();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      close();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(Math.min(activeIndex + 1, Math.max(shown.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(Math.max(activeIndex - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (shown[activeIndex]) pick(shown[activeIndex]);
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={listId}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className={cn(
          'flex h-9 w-full items-center justify-between gap-2 rounded-md border border-border bg-white px-3 text-left text-sm',
          'hover:bg-gray-50 focus:outline-none focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50',
          open && 'border-primary',
        )}
      >
        <span className={cn('min-w-0 flex-1 truncate', !shownLabel && 'text-gray-400')}>
          {shownLabel || placeholder}
        </span>
        {loading && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-gray-400" />}
        <ChevronDown className={cn('h-4 w-4 shrink-0 text-gray-400 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute z-[120] mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg" onKeyDown={onKeyDown}>
          <div className="flex items-center gap-2 border-b border-gray-100 px-3">
            <Search className="h-4 w-4 shrink-0 text-gray-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => updateQuery(e.target.value)}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              aria-controls={listId}
              aria-activedescendant={shown[activeIndex] ? optionId(activeIndex) : undefined}
              maxLength={searchMaxLength}
              className="h-9 flex-1 text-sm outline-none"
            />
          </div>
          <ul ref={listRef} id={listId} role="listbox" aria-label={ariaLabel} aria-busy={loading}
            className={cn('overflow-y-auto py-1', footer ? 'max-h-[212px]' : 'max-h-[240px]', loading && shown.length > 0 && 'opacity-60')}>
            {loading && shown.length === 0 && (
              <li className="flex items-center gap-2 px-3 py-2 text-xs text-gray-400">
                <Loader2 className="h-3 w-3 animate-spin" /> Loading…
              </li>
            )}
            {!loading && shown.length === 0 && <li className="break-words px-3 py-2 text-xs text-gray-400">{emptyText}</li>}
            {shown.map((option, i) => (
              <li key={option.value} id={optionId(i)} role="option" aria-selected={option.value === value}>
                <button
                  type="button"
                  onClick={() => pick(option)}
                  onMouseEnter={() => setActive(i)}
                  className={cn('flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm', i === activeIndex && 'bg-gray-50')}
                >
                  <Check className={cn('h-3.5 w-3.5 shrink-0', option.value === value ? 'text-primary' : 'invisible')} />
                  <span className="min-w-0 flex-1 truncate">{option.label}</span>
                  {option.hint && <span className="shrink-0 text-[11px] text-gray-400">{option.hint}</span>}
                </button>
              </li>
            ))}
          </ul>
          {footer && <div className="border-t border-gray-100 px-3 py-1.5 text-[11px] text-gray-400">{footer}</div>}
        </div>
      )}
    </div>
  );
}
